'use client';

import { c as _c } from "react/compiler-runtime";
import type { AutosizeTextAreaRef } from '@/components/textarea/types';
import type { PrV2CommentAnchorKind } from '@/lib/pr-v2/comment-anchors';
import type { PrV2CommentResponse, PrV2ReactionContent, PrV2ReviewThread } from '@/lib/pr-v2/types';
import { Bot, Check, ChevronsUpIcon, Copy, Eye, FoldVertical, Link2, Lock, MoreHorizontal, Pencil, Reply, SmilePlus, Sparkles, Trash2, UnfoldVertical } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import type { PrV2ApplySuggestionPayload } from '../../comments/pr-v2-suggestion-block';
import type { PrV2ThreadAiChatSurface } from './pr-v2-thread-ai-chat';
import { PrV2CommentContent } from '../../comments/pr-v2-comment-content';
import { extractAiAgentPromptText, getSuggestionOriginalLines } from '../../comments/pr-v2-comment-utils';
import { PrV2ThreadCodePreview } from '../../pr-v2-thread-code-preview';
import { PrV2FilePathLabel } from '../pr-v2-file-path-label';
import { PrV2UserNameLabel } from '../pr-v2-user-name-label';
import { usePrV2RepositoryIdResolution } from '../use-pr-v2-repository-id';
import { buildPrV2ThreadAiChatText } from './pr-v2-thread-ai-chat';
import { useExpandResolvedOnReveal } from './use-expand-resolved-on-reveal';
import { useResetResolvedStateOnThreadRecycled } from './use-reset-resolved-state-on-thread-recycled';
import { CodingAgentsUpgradeTooltipContent } from '@/components/billing/coding-agents-upgrade-tooltip';
import { AutosizeTextarea } from '@/components/textarea/autosize-textarea';
import { useMarkdownLinkPaste } from '@/components/textarea/hooks/use-markdown-link-paste';
import { TEXTAREA_VARIANTS } from '@/components/textarea/types';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { useSidebarActions } from '@/components/ui/sidebar';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useQueryAIReviewSettings } from '@/data/ai-review/settings/use-query-ai-review-settings';
import { useCodingAgentsAccess } from '@/data/billing/use-coding-agents-access';
import { severityToPriority } from '@/lib/ai-review-priority';
import { DEFAULT_AUTO_FIX_PROVIDER } from '@/lib/ai-review-settings/schema';
import { extractCommentMetadata, removeMetadataFromComment } from '@/lib/ai/code-review-service/utils/metadata';
import { FIX_TYPE } from '@/lib/ai/coding-agent-service/fix-tracking';
import { FIX_BUTTON_SOURCE, trackFixButtonClicked } from '@/lib/analytics/fix-button-clicked';
import { buildCommentContext } from '@/lib/comments/build-comment-context';
import { FIX_SESSIONS_PATH } from '@/lib/fix-sessions/paths';
import { getPrV2CommentAnchorId, getPrV2CommentAnchorTab, setPrV2CommentLink } from '@/lib/pr-v2/comment-anchors';
import { getPrV2CommentLifecycle, getPrV2CommentTransitionKey } from '@/lib/pr-v2/comment-lifecycle';
import { resolveFixProviderButtonCopy } from '@/lib/pr-v2/fix-provider-button-copy';
import { isPendingGithubCommentId } from '@/lib/pr-v2/pending-comment-ids';
import { getStableViewKey } from '@/lib/pr-v2/stable-view-keys';
import { PR_V2_REACTION_CONTENTS } from '@/lib/pr-v2/types';
import { usePrStore } from '@/lib/stores/pr-store';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import { copyToClipboard } from '@/utils/clipboard';
import { isMrgeBot } from '@/utils/github/bot-detection';
const THREAD_METADATA_PILL_CLASSNAME = 'inline-flex items-center rounded-full border border-zinc-700/30 px-1.5 py-0.5 text-[9px] font-medium text-zinc-400';
const THREAD_ACTION_ICON_CLASSNAME = 'size-3.5';
export type PrV2ThreadReactionsProps = {
  isPending: boolean;
  onToggleReaction: (comment: PrV2CommentResponse, emoji: PrV2ReactionContent) => void;
  getReactionCount: (comment: PrV2CommentResponse, emoji: PrV2ReactionContent) => number;
  getReactionUsers: (comment: PrV2CommentResponse, emoji: PrV2ReactionContent) => string[];
  isReactionActive: (comment: PrV2CommentResponse, emoji: PrV2ReactionContent) => boolean;
  isPendingForComment: (comment: Pick<PrV2CommentResponse, 'githubCommentId'>) => boolean;
  isPendingForReaction?: (comment: Pick<PrV2CommentResponse, 'githubCommentId'>, emoji: PrV2ReactionContent) => boolean;
  canReactToComment?: (comment: PrV2CommentResponse) => boolean;
};
export type PrV2ThreadCardMutationState = {
  any: boolean;
  reply: boolean;
  edit: boolean;
  delete: boolean;
  resolve: boolean;
};
export type PrV2ThreadHeadline = {
  text: string;
  align?: 'center';
  className?: string;
  copyText?: string;
  filePath?: boolean;
  truncateFromStart?: boolean;
};
type PrV2ResolvedPresentation = 'none' | 'hybrid';
type PrV2CommentManageMode = 'persisted' | 'pending' | 'none';
type PrV2ReplyComposerPlacement = 'inline-comment' | 'thread-footer';
type PrV2ThreadCodePreviewMode = 'none' | 'discussion-inline';
type PrV2ReactionAffordancePlacement = 'footer-row' | 'hover-strip';
export type PrV2ThreadCardAiPromptContext = {
  oldFileContent?: string | null;
  newFileContent?: string | null;
  diffHunk?: string | null;
  /**
   * Resolves full file contents on demand for stripped payloads (contents omitted from the
   * code-changes response). Callers resolve immediately from in-memory contents when available,
   * so awaiting this is free outside the omitted-contents case.
   */
  loadFileContents?: () => Promise<{
    oldFileContent: string | null;
    newFileContent: string | null;
  } | null>;
  /**
   * Cheap synchronous signal for whether `loadFileContents` resolves without a network fetch.
   * The no-ClipboardItem clipboard fallback must decide synchronously (inside the click's
   * transient user activation) whether awaiting `loadFileContents` is safe; when this is not
   * `true` the fallback skips content loading and copies the diff-hunk-only prompt instead.
   */
  hasImmediateFileContents?: boolean;
};
type PrV2ThreadCardReplyProps = {
  /**
   * Reply behavior is always explicit so callers cannot accidentally drop
   * reply affordances by omitting one piece of reply state wiring.
   */
  activeReplyCommentId: string | null;
  replyDraftKey: string;
  replyDraft: string;
  onReplyDraftChange: (value: string) => void;
  onReplySubmit: (githubCommentId: string, body: string, threadDraftKey: string) => Promise<void> | void;
  onReplySelect: (comment: PrV2CommentResponse) => void;
  onReplyCancel: () => void;
  canReplyToComment: (comment: PrV2CommentResponse) => boolean;
  allowPendingReplyTargets?: boolean;
};
type PrV2ThreadCardProps = {
  owner: string;
  repo: string;
  pullNumber: string;
  /**
   * Set only on the public page of a public repository, whose viewer may be signed out, so the
   * comments also render without authenticated GitHub calls.
   */
  isReadOnly?: boolean;
  comments: PrV2CommentResponse[];
  thread?: PrV2ReviewThread;
  severityByCommentId?: Record<string, number>;
  headline?: PrV2ThreadHeadline;
  anchorKind: PrV2CommentAnchorKind;
  layout?: 'inline' | 'full';
  isSplitDiffStyle: boolean;
  replyPlaceholder?: string;
  replyComposerStyle?: 'default' | 'discussion-v1';
  replyComposerPlacement?: PrV2ReplyComposerPlacement;
  resolvedPresentation?: PrV2ResolvedPresentation;
  isMutating: boolean;
  mutationState: PrV2ThreadCardMutationState;
  onResolveToggle: (comment: PrV2CommentResponse, resolve: boolean) => Promise<void> | void;
  onEditComment?: (comment: PrV2CommentResponse, body: string) => Promise<void> | void;
  onDeleteComment?: (comment: PrV2CommentResponse) => Promise<void> | void;
  onEditPendingComment?: (comment: PrV2CommentResponse, body: string) => Promise<void> | void;
  onDeletePendingComment?: (comment: PrV2CommentResponse) => Promise<void> | void;
  onApplySuggestion?: (comment: PrV2CommentResponse, payload: PrV2ApplySuggestionPayload) => Promise<void> | void;
  suggestionApplyDisabledReason?: string;
  onSeeInChanges?: (comment: PrV2CommentResponse) => void;
  shouldShowSeeInChanges?: (comment: PrV2CommentResponse) => boolean;
  reactions?: PrV2ThreadReactionsProps;
  reactionAffordancePlacement?: PrV2ReactionAffordancePlacement;
  aiChatSurface?: PrV2ThreadAiChatSurface;
  overflowUtilityActions?: boolean;
  codePreviewMode?: PrV2ThreadCodePreviewMode;
  aiPromptContext?: PrV2ThreadCardAiPromptContext;
  getEmptyTextForComment?: (comment: PrV2CommentResponse) => string | undefined;
} & PrV2ThreadCardReplyProps;
const THREAD_COMMENT_MARKDOWN_CLASS = 'mt-0.5 overflow-visible text-[13px] leading-[1.5] text-zinc-300 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-0.5 [&_p]:text-[13px] [&_p]:leading-[1.5] [&_p:first-child:has(>img:only-child)]:my-0 [&_p:first-child_img:only-child]:my-0 [&_ul]:my-0.5 [&_ol]:my-0.5 [&_li]:my-0 [&_li]:text-[13px] [&_blockquote]:my-0.5 [&_pre]:my-1.5 [&_pre]:border [&_pre]:border-zinc-800/80 [&_pre]:bg-[#101012] [&_pre]:text-[12px] [&_pre]:font-sans [&_pre]:overflow-x-auto [&_code]:text-[12px] [&_code]:font-sans [&_hr]:hidden [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-[13px] [&_h4]:text-[13px] [&_img]:my-2 [&_img]:max-h-[200px] [&_img]:object-contain [&_img]:object-left [&_br]:leading-[0.5] [&_table]:my-2 [&_th]:align-middle [&_td]:align-middle [&_td_p]:my-0 [&_td_img]:my-0 [&_td_img]:inline-block [&_td_img]:h-[1em] [&_td_img]:w-auto [&_td_img]:align-middle [&_td_img]:rounded-none [&_pre_.token.comment]:text-[#84848A] [&_pre_.token.keyword]:text-[#ff678d] [&_pre_.token.string]:text-[#5ecc71] [&_pre_.token.number]:text-[#68cdf2] [&_pre_.token.function]:text-[#9d6afb] [&_pre_.token.class-name]:text-[#d568ea] [&_pre_.token.variable]:text-[#ffa359] [&_pre_.token.property]:text-[#ffa359] [&_pre_.token.operator]:text-[#08c0ef] [&_pre_.token.punctuation]:text-[#79797F]';
const AVATAR_COLORS = ['bg-zinc-600', 'bg-zinc-700', 'bg-zinc-500', 'bg-zinc-800', 'bg-neutral-600', 'bg-neutral-700', 'bg-stone-600', 'bg-stone-700'] as const;
const REACTION_EMOJIS = PR_V2_REACTION_CONTENTS;
const MAX_REACTION_TOOLTIP_USERS = 10;
const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const REACTION_EMOJI_LABELS: Record<PrV2ReactionContent, {
  emoji: string;
  label: string;
}> = {
  '+1': {
    emoji: '👍',
    label: 'thumbs up'
  },
  '-1': {
    emoji: '👎',
    label: 'thumbs down'
  },
  'laugh': {
    emoji: '😂',
    label: 'laugh'
  },
  'confused': {
    emoji: '🤔',
    label: 'confused'
  },
  'heart': {
    emoji: '❤️',
    label: 'heart'
  },
  'hooray': {
    emoji: '🎉',
    label: 'hooray'
  },
  'rocket': {
    emoji: '🚀',
    label: 'rocket'
  },
  'eyes': {
    emoji: '👀',
    label: 'eyes'
  }
};
function ThreadTooltip(t0) {
  const $ = _c(16);
  let children;
  let content;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      content,
      children,
      align: t1,
      side: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = content;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    content = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const align = t1 === undefined ? "center" : t1;
  const side = t2 === undefined ? "top" : t2;
  let t3;
  if ($[6] !== children) {
    t3 = <TooltipTrigger asChild={true}>{children}</TooltipTrigger>;
    $[6] = children;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== align || $[9] !== content || $[10] !== side) {
    t4 = <TooltipContent align={align} side={side}>{content}</TooltipContent>;
    $[8] = align;
    $[9] = content;
    $[10] = side;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props || $[13] !== t3 || $[14] !== t4) {
    t5 = <Tooltip {...props}>{t3}{t4}</Tooltip>;
    $[12] = props;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function formatReactionTooltipUsers(users: string[]): string | null {
  if (users.length === 0) {
    return null;
  }
  if (users.length > MAX_REACTION_TOOLTIP_USERS) {
    return `${users.slice(0, MAX_REACTION_TOOLTIP_USERS).join(', ')} and ${users.length - MAX_REACTION_TOOLTIP_USERS} more`;
  }
  return users.join(', ');
}
function formatReactionTooltipTitle(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}
function ThreadHeadlineLabel(t0) {
  const $ = _c(25);
  const {
    headline
  } = t0;
  const isCentered = headline.align === "center";
  let t1;
  if ($[0] !== headline.copyText || $[1] !== headline.filePath || $[2] !== headline.text || $[3] !== headline.truncateFromStart) {
    t1 = headline.filePath ? <PrV2FilePathLabel filename={headline.text} title={headline.copyText ?? headline.text} className="inline-flex max-w-full gap-1.5" basenameClassName="text-[11px] text-zinc-400" directoryClassName="text-[11px] text-zinc-500" /> : headline.truncateFromStart ? <span className="block truncate" style={{
      direction: "rtl",
      textAlign: "left"
    }}>{headline.text}</span> : <span className="block truncate">{headline.text}</span>;
    $[0] = headline.copyText;
    $[1] = headline.filePath;
    $[2] = headline.text;
    $[3] = headline.truncateFromStart;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const textNode = t1;
  const copyText = headline.copyText;
  if (!copyText) {
    const t2 = isCentered && "inline-block";
    let t3;
    if ($[5] !== headline.className || $[6] !== t2) {
      t3 = cn("min-w-0 max-w-full text-[11px] text-zinc-500", t2, headline.className);
      $[5] = headline.className;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t3 || $[9] !== textNode) {
      t4 = <p className={t3}>{textNode}</p>;
      $[8] = t3;
      $[9] = textNode;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t2;
  if ($[11] !== copyText) {
    t2 = <span className="block max-w-[80vw] break-all">{copyText}</span>;
    $[11] = copyText;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const t3 = `Copy file reference ${copyText}`;
  let t4;
  if ($[13] !== headline.className) {
    t4 = cn("inline-block max-w-full cursor-pointer text-left text-[11px] text-zinc-500 transition-colors hover:text-zinc-300 focus-visible:text-zinc-300 focus-visible:outline-none focus-visible:underline", headline.className);
    $[13] = headline.className;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== copyText) {
    t5 = () => void copyToClipboard(copyText, {
      successMessage: "File reference copied",
      errorMessage: "Failed to copy file reference"
    });
    $[15] = copyText;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== textNode) {
    t6 = <button type="button" aria-label={t3} className={t4} onClick={t5}>{textNode}</button>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = textNode;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t2 || $[23] !== t6) {
    t7 = <ThreadTooltip content={t2}>{t6}</ThreadTooltip>;
    $[22] = t2;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}
function normalizeIdentifier(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

/**
 * Returns a normalized identity set for the signed-in user so we can match
 * authored comments regardless of which profile field is populated.
 */
function getCurrentUserIdentifiers(user: {
  email?: string | null;
  user_metadata?: unknown;
} | null): Set<string> {
  const identifiers = new Set<string>();
  const metadata = asRecord(user?.user_metadata);
  const metadataCandidates = [metadata?.user_name, metadata?.name, metadata?.preferred_username, metadata?.login];
  metadataCandidates.forEach(candidate => {
    const normalized = normalizeIdentifier(candidate);
    if (normalized) {
      identifiers.add(normalized);
    }
  });
  const normalizedEmail = normalizeIdentifier(user?.email);
  if (normalizedEmail) {
    identifiers.add(normalizedEmail);
  }
  return identifiers;
}
function parsePositiveInteger(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  if (Number.isInteger(parsed) && parsed > 0) {
    return parsed;
  }
  return null;
}
function canReplyToCommentId(githubCommentId: string, allowPendingReplyTargets: boolean): boolean {
  return parsePositiveInteger(githubCommentId) != null || allowPendingReplyTargets && isPendingGithubCommentId(githubCommentId);
}

/**
 * We expose controls only for authored comments. Persisted comments route
 * through API mutations; pending comments route through local pending state.
 */
function getCommentManageMode(comment: PrV2CommentResponse, userIdentifiers: Set<string>): PrV2CommentManageMode {
  const commentAuthor = normalizeIdentifier(comment.userLogin);
  if (!commentAuthor) {
    return 'none';
  }
  if (!userIdentifiers.has(commentAuthor)) {
    return 'none';
  }
  if (getPrV2CommentLifecycle(comment) === 'queued') {
    return 'pending';
  }
  if (parsePositiveInteger(comment.githubCommentId) != null) {
    return 'persisted';
  }
  return 'none';
}
function getAvatarColor(login: string): string {
  let hash = 0;
  for (let i = 0; i < login.length; i++) {
    hash = hash * 31 + login.charCodeAt(i) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? AVATAR_COLORS[0];
}
export function getCommentCreatedAt(comment: PrV2CommentResponse): string | null {
  return comment.createdAtGithub ?? comment.updatedAtGithub;
}
function getCommentRenderKey(comment: PrV2CommentResponse): string {
  const transitionKey = getPrV2CommentTransitionKey(comment);
  // Pending and optimistic submit variants of the same local comment should reuse one
  // render key so the thread body does not remount during the queued -> submitting handoff.
  return getStableViewKey(transitionKey ?? comment.githubCommentId);
}
export function formatCommentAge(value: string | null): string {
  if (!value) {
    return '';
  }
  const createdAt = new Date(value).getTime();
  if (!Number.isFinite(createdAt)) {
    return '';
  }
  const deltaMinutes = Math.floor((Date.now() - createdAt) / MS_PER_MINUTE);
  if (deltaMinutes < 1) {
    return 'now';
  }
  if (deltaMinutes < MINUTES_PER_HOUR) {
    return `${deltaMinutes}m`;
  }
  const deltaHours = Math.floor(deltaMinutes / MINUTES_PER_HOUR);
  if (deltaHours < HOURS_PER_DAY) {
    return `${deltaHours}h`;
  }
  const deltaDays = Math.floor(deltaHours / HOURS_PER_DAY);
  if (deltaDays < DAYS_PER_WEEK) {
    return `${deltaDays}d`;
  }
  return `${Math.floor(deltaDays / DAYS_PER_WEEK)}w`;
}
function formatCommentTimestamp(value: string | null): string {
  if (!value) {
    return 'Unknown time';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown time';
  }
  return date.toLocaleString();
}
function getCommentConfidence(body: string | null): number | undefined {
  if (!body) {
    return undefined;
  }
  return extractCommentMetadata(body)?.confidence;
}
const GENERATING_FIX_MESSAGE = 'Generating fix – this may take a few minutes';
function removeLeadingPriorityPrefix(body: string | null, priority: string | null): string | null {
  if (!body || !priority) {
    return body;
  }
  const match = body.match(/^(\s*(?:<!--[\s\S]*?-->\s*)*)(P[0-3])\s*:\s*/i);
  if (!match || match[2]?.toUpperCase() !== priority) {
    return body;
  }
  return `${match[1] ?? ''}${body.slice(match[0].length)}`;
}
function getVisibleReactionEmojis(comment: PrV2CommentResponse, reactions: PrV2ThreadReactionsProps): PrV2ReactionContent[] {
  return REACTION_EMOJIS.filter(emoji => {
    const count = reactions.getReactionCount(comment, emoji);
    const isActive = reactions.isReactionActive(comment, emoji);
    return count > 0 || isActive;
  });
}
function getActiveReactionEmojis(comment: PrV2CommentResponse, reactions: PrV2ThreadReactionsProps): PrV2ReactionContent[] {
  return REACTION_EMOJIS.filter(emoji => reactions.isReactionActive(comment, emoji));
}
function ReactionPickerTrigger(t0) {
  const $ = _c(27);
  const {
    comment,
    reactions,
    variant: t1,
    pickerAlign: t2,
    pickerSide: t3,
    tooltipContent
  } = t0;
  const variant = t1 === undefined ? "chip" : t1;
  const pickerAlign = t2 === undefined ? "start" : t2;
  const pickerSide = t3 === undefined ? "top" : t3;
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  let t4;
  if ($[0] !== comment || $[1] !== reactions) {
    t4 = getActiveReactionEmojis(comment, reactions);
    $[0] = comment;
    $[1] = reactions;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const activeReactionEmojis = t4;
  let t5;
  let t6;
  if ($[3] !== isPickerOpen) {
    t5 = () => {
      if (!isPickerOpen) {
        return;
      }
      const handlePointerDown = event => {
        const pickerElement = pickerRef.current;
        if (!pickerElement) {
          return;
        }
        const target = event.target;
        if (target instanceof Node && pickerElement.contains(target)) {
          return;
        }
        setIsPickerOpen(false);
      };
      window.addEventListener("pointerdown", handlePointerDown);
      return () => {
        window.removeEventListener("pointerdown", handlePointerDown);
      };
    };
    t6 = [isPickerOpen];
    $[3] = isPickerOpen;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  const t7 = variant === "icon" ? "inline-flex size-6 items-center justify-center rounded-md border border-transparent transition-colors" : "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[11px] transition-colors";
  const t8 = isPickerOpen ? variant === "icon" ? "bg-zinc-800/40 text-zinc-300 hover:bg-zinc-800/60" : "border-zinc-500 bg-zinc-800 text-zinc-100" : variant === "icon" ? "text-zinc-500 hover:bg-zinc-800/50 hover:text-zinc-300" : "border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-200";
  let t9;
  if ($[6] !== t7 || $[7] !== t8) {
    t9 = cn(t7, t8);
    $[6] = t7;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setIsPickerOpen(_temp);
    t11 = <SmilePlus className="size-3.5" />;
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  let t12;
  if ($[11] !== isPickerOpen || $[12] !== t9) {
    t12 = <button type="button" aria-label="Add reaction" aria-haspopup="menu" aria-expanded={isPickerOpen} className={t9} onClick={t10}>{t11}</button>;
    $[11] = isPickerOpen;
    $[12] = t9;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const button = t12;
  let t13;
  if ($[14] !== button || $[15] !== tooltipContent) {
    t13 = tooltipContent ? <ThreadTooltip content={tooltipContent}>{button}</ThreadTooltip> : button;
    $[14] = button;
    $[15] = tooltipContent;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== activeReactionEmojis || $[18] !== comment || $[19] !== isPickerOpen || $[20] !== pickerAlign || $[21] !== pickerSide || $[22] !== reactions) {
    t14 = isPickerOpen ? <div className={cn("absolute z-20 flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 p-1 shadow-lg", pickerSide === "bottom" ? "top-full mt-1" : "bottom-full mb-1", pickerAlign === "end" ? "right-0" : "left-0")}>{REACTION_EMOJIS.map(emoji => {
        const {
          emoji: emojiChar,
          label
        } = REACTION_EMOJI_LABELS[emoji];
        const count = reactions.getReactionCount(comment, emoji);
        const isActive = activeReactionEmojis.includes(emoji);
        const isReactionPending = reactions.isPendingForReaction?.(comment, emoji) ?? reactions.isPendingForComment(comment);
        return <button key={`${comment.id}-picker-${emoji}`} type="button" aria-label={count > 0 ? `React with ${label} (${count})` : `React with ${label}`} aria-pressed={isActive} className={cn("inline-flex h-7 w-7 items-center justify-center rounded text-sm transition-colors", isActive ? "bg-zinc-700 text-zinc-100" : "text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100")} disabled={isReactionPending} onClick={() => {
          reactions.onToggleReaction(comment, emoji);
          setIsPickerOpen(false);
        }}>{emojiChar}</button>;
      })}</div> : null;
    $[17] = activeReactionEmojis;
    $[18] = comment;
    $[19] = isPickerOpen;
    $[20] = pickerAlign;
    $[21] = pickerSide;
    $[22] = reactions;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t13 || $[25] !== t14) {
    t15 = <div ref={pickerRef} className="relative">{t13}{t14}</div>;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}
function _temp(current) {
  return !current;
}
function ReactionFooter(t0) {
  const $ = _c(19);
  const {
    comment,
    reactions,
    allowToggleReactions,
    showAddReactionTrigger
  } = t0;
  let t1;
  if ($[0] !== comment || $[1] !== reactions) {
    t1 = getVisibleReactionEmojis(comment, reactions);
    $[0] = comment;
    $[1] = reactions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const visibleReactions = t1;
  if (visibleReactions.length === 0 && !showAddReactionTrigger) {
    return null;
  }
  let t2;
  if ($[3] !== allowToggleReactions || $[4] !== comment || $[5] !== reactions || $[6] !== visibleReactions) {
    let t3;
    if ($[8] !== allowToggleReactions || $[9] !== comment || $[10] !== reactions) {
      t3 = emoji => {
        const count = reactions.getReactionCount(comment, emoji);
        const reactionUsers = reactions.getReactionUsers(comment, emoji);
        const isActive = reactions.isReactionActive(comment, emoji);
        const isReactionPending = reactions.isPendingForReaction?.(comment, emoji) ?? reactions.isPendingForComment(comment);
        const {
          emoji: emojiChar,
          label
        } = REACTION_EMOJI_LABELS[emoji];
        const tooltipTitle = formatReactionTooltipTitle(label);
        const tooltipUsers = formatReactionTooltipUsers(reactionUsers);
        const reactionAriaLabel = count > 0 ? `React with ${label} (${count})` : `React with ${label}`;
        const reactionButton = <button type="button" aria-label={reactionAriaLabel} aria-pressed={isActive} className={`inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[11px] transition-colors ${isActive ? "border-zinc-500 bg-zinc-800 text-zinc-100" : allowToggleReactions ? "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100" : "border-zinc-700 bg-zinc-900 text-zinc-300"}`} disabled={!allowToggleReactions || isReactionPending} onClick={() => {
          if (!allowToggleReactions) {
            return;
          }
          reactions.onToggleReaction(comment, emoji);
        }}><span>{emojiChar}</span>{count > 0 ? <span className="ml-1 tabular-nums">{count}</span> : null}</button>;
        return <ThreadTooltip key={`${comment.id}-${emoji}`} content={<div className="flex max-w-[20rem] flex-col gap-1 p-0"><div className="text-xs font-medium">{tooltipTitle}</div>{tooltipUsers ? <div className="text-xs text-zinc-400">{tooltipUsers}</div> : null}</div>}>{reactionButton}</ThreadTooltip>;
      };
      $[8] = allowToggleReactions;
      $[9] = comment;
      $[10] = reactions;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = visibleReactions.map(t3);
    $[3] = allowToggleReactions;
    $[4] = comment;
    $[5] = reactions;
    $[6] = visibleReactions;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[12] !== comment || $[13] !== reactions || $[14] !== showAddReactionTrigger) {
    t3 = showAddReactionTrigger ? <ReactionPickerTrigger comment={comment} reactions={reactions} variant="chip" /> : null;
    $[12] = comment;
    $[13] = reactions;
    $[14] = showAddReactionTrigger;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== t2 || $[17] !== t3) {
    t4 = <div className="relative mt-2 flex items-center gap-1.5">{t2}{t3}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}

// eslint-disable-next-line complexity
export function PrV2ThreadCard({
  owner,
  repo,
  pullNumber,
  isReadOnly = false,
  comments,
  thread,
  severityByCommentId,
  headline,
  anchorKind,
  layout = 'full',
  isSplitDiffStyle,
  replyPlaceholder = 'Reply...',
  replyComposerStyle = 'default',
  replyComposerPlacement = 'inline-comment',
  reactionAffordancePlacement = 'footer-row',
  resolvedPresentation = 'none',
  isMutating,
  mutationState,
  replyDraftKey,
  replyDraft,
  activeReplyCommentId,
  onReplyDraftChange,
  onReplySubmit,
  onReplySelect,
  onReplyCancel,
  canReplyToComment,
  allowPendingReplyTargets = false,
  onResolveToggle,
  onEditComment,
  onDeleteComment,
  onEditPendingComment,
  onDeletePendingComment,
  onApplySuggestion,
  suggestionApplyDisabledReason: suggestionApplyDisabledReasonOverride,
  onSeeInChanges,
  shouldShowSeeInChanges,
  reactions,
  aiChatSurface = 'changes',
  overflowUtilityActions = false,
  codePreviewMode = 'none',
  aiPromptContext,
  getEmptyTextForComment
}: PrV2ThreadCardProps) {
  const anchorTab = getPrV2CommentAnchorTab(anchorKind);
  const {
    user
  } = useAuth();
  const {
    toggleSidebar
  } = useSidebarActions();
  const {
    installationId,
    repositoryId: storeRepositoryId
  } = usePrStore();
  const {
    repositoryId: fallbackRepositoryId,
    repositoryIsPrivate
  } = usePrV2RepositoryIdResolution({
    owner,
    repo,
    installationId,
    enabled: Boolean(installationId)
  });
  const repositoryId = storeRepositoryId ?? fallbackRepositoryId;
  const hasRepositoryId = repositoryId != null;
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [pendingDeleteComment, setPendingDeleteComment] = useState<PrV2CommentResponse | null>(null);
  const [applyingSuggestionCommentId, setApplyingSuggestionCommentId] = useState<string | null>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const handleEditPaste = useMarkdownLinkPaste({
    onChange: setEditDraft,
    textareaRef: editTextareaRef
  });
  const deleteConfirmRef = useRef<HTMLButtonElement>(null);
  const deleteDialogContentRef = useRef<HTMLDivElement>(null);
  const replyTextareaRef = useRef<AutosizeTextAreaRef | null>(null);
  const shouldRefocusReplyOnRestoreRef = useRef(false);
  const previousResolvedThreadIdRef = useRef<string | null>(thread?.id ?? null);
  const previousEffectiveResolvedRef = useRef(Boolean(thread?.isResolved));
  const [isReplySubmitting, setIsReplySubmitting] = useState(false);
  const [isResolvedExpanded, setIsResolvedExpanded] = useState(!(thread?.isResolved ?? false));
  const [localResolvedOverride, setLocalResolvedOverride] = useState<boolean | null>(null);
  const serverResolved = Boolean(thread?.isResolved);
  const wasRecycled = useResetResolvedStateOnThreadRecycled({
    comments,
    thread,
    previousResolvedThreadIdRef,
    previousEffectiveResolvedRef,
    serverResolved,
    setLocalResolvedOverride,
    setIsResolvedExpanded
  });
  // Immediately hide deleted comments in the local view so deletion feels instant.
  const [locallyHiddenCommentIds, setLocallyHiddenCommentIds] = useState<Set<string>>(new Set());
  // Track in-flight deletes by comment so one delete does not block unrelated comments.
  const [deletingCommentIds, setDeletingCommentIds] = useState<Set<string>>(new Set());
  const [prevCommentsForIdPrune, setPrevCommentsForIdPrune] = useState(comments);

  // Prune ids whose comments no longer exist so a re-used githubCommentId can't
  // be wrongly hidden or marked deleting later. Adjusted during render (with a
  // prev-comparison) instead of an effect to avoid a post-paint cascading render.
  if (prevCommentsForIdPrune !== comments) {
    setPrevCommentsForIdPrune(comments);
    const availableCommentIds = new Set(comments.map(comment => comment.githubCommentId));
    const pruneMissingIds = (previousIds: Set<string>) => {
      let hasChanges = false;
      const nextIds = new Set<string>();
      previousIds.forEach(commentId => {
        if (!availableCommentIds.has(commentId)) {
          hasChanges = true;
          return;
        }
        nextIds.add(commentId);
      });
      return hasChanges ? nextIds : previousIds;
    };
    setLocallyHiddenCommentIds(pruneMissingIds);
    setDeletingCommentIds(pruneMissingIds);
  }
  const {
    settings: aiReviewSettings
  } = useQueryAIReviewSettings({
    installationId,
    repositoryId,
    repositoryIsPrivate,
    enabled: hasRepositoryId
  });
  const fixProviderCopy = resolveFixProviderButtonCopy(aiReviewSettings?.autoFixProvider ?? DEFAULT_AUTO_FIX_PROVIDER);
  const {
    canUseCodingAgents,
    isError: isCodingAgentsAccessError,
    isLoading: isCodingAgentsAccessLoading
  } = useCodingAgentsAccess(installationId);
  const currentUserIdentifiers = useMemo(() => getCurrentUserIdentifiers(user), [user]);
  const visibleComments = useMemo(() => comments.filter(comment_0 => !locallyHiddenCommentIds.has(comment_0.githubCommentId)), [comments, locallyHiddenCommentIds]);
  const hasTransientThreadGap = !thread && previousResolvedThreadIdRef.current !== null;
  const activeLocalOverride = wasRecycled ? null : localResolvedOverride;
  const effectiveIsResolved = activeLocalOverride ?? (hasTransientThreadGap ? previousEffectiveResolvedRef.current : serverResolved);
  const shouldRenderResolvedSummary = resolvedPresentation === 'hybrid' && effectiveIsResolved;
  const effectiveMutationState = useMemo<PrV2ThreadCardMutationState>(() => {
    const any = mutationState.any || isMutating;
    return {
      any,
      reply: mutationState.reply || any,
      edit: mutationState.edit || any,
      delete: mutationState.delete || any,
      resolve: mutationState.resolve || any
    };
  }, [isMutating, mutationState]);
  const isEditActionPending = effectiveMutationState.edit || effectiveMutationState.delete;
  const isReplyActionPending = effectiveMutationState.reply || effectiveMutationState.edit || effectiveMutationState.delete || isReplySubmitting;
  const handleStartEditing = useCallback((comment_1: PrV2CommentResponse) => {
    setEditingCommentId(comment_1.githubCommentId);
    setEditDraft(comment_1.body ?? '');
  }, []);
  const handleCancelEditing = useCallback(() => {
    setEditingCommentId(null);
    setEditDraft('');
  }, []);
  const handleSubmitEdit = useCallback(async () => {
    if (!editingCommentId || isEditActionPending) {
      return;
    }
    const targetComment = comments.find(comment_2 => comment_2.githubCommentId === editingCommentId);
    if (!targetComment) {
      return;
    }
    const nextBody = editDraft.trim();
    if (!nextBody) {
      return;
    }
    const manageMode = getCommentManageMode(targetComment, currentUserIdentifiers);
    const editHandler = manageMode === 'pending' ? onEditPendingComment : manageMode === 'persisted' ? onEditComment : undefined;
    if (!editHandler) {
      return;
    }

    // Close immediately so edit feels optimistic; rollback re-opens with draft on failure.
    setEditingCommentId(null);
    setEditDraft('');
    try {
      await editHandler(targetComment, nextBody);
    } catch (error) {
      // Restore editor/draft so users can retry without losing text.
      setEditingCommentId(targetComment.githubCommentId);
      setEditDraft(nextBody);
      toast.error(error instanceof Error ? error.message : 'Failed to save comment');
    }
  }, [comments, currentUserIdentifiers, editDraft, editingCommentId, isEditActionPending, onEditComment, onEditPendingComment]);
  const handleRequestDelete = useCallback((comment_3: PrV2CommentResponse) => {
    if (mutationState.edit || deletingCommentIds.has(comment_3.githubCommentId)) {
      return;
    }
    const manageMode_0 = getCommentManageMode(comment_3, currentUserIdentifiers);
    if (manageMode_0 === 'persisted' && !onDeleteComment || manageMode_0 === 'pending' && !onDeletePendingComment || manageMode_0 === 'none') {
      return;
    }
    setPendingDeleteComment(comment_3);
  }, [currentUserIdentifiers, deletingCommentIds, mutationState.edit, onDeleteComment, onDeletePendingComment]);
  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteComment) {
      return;
    }
    const targetComment_0 = pendingDeleteComment;
    if (mutationState.edit || deletingCommentIds.has(targetComment_0.githubCommentId)) {
      return;
    }
    const manageMode_1 = getCommentManageMode(targetComment_0, currentUserIdentifiers);
    const deleteHandler = manageMode_1 === 'pending' ? onDeletePendingComment : manageMode_1 === 'persisted' ? onDeleteComment : undefined;
    if (!deleteHandler) {
      return;
    }
    setPendingDeleteComment(null);
    setDeletingCommentIds(previousIds_0 => {
      const nextIds_0 = new Set(previousIds_0);
      nextIds_0.add(targetComment_0.githubCommentId);
      return nextIds_0;
    });
    setLocallyHiddenCommentIds(previousIds_1 => {
      const nextIds_1 = new Set(previousIds_1);
      nextIds_1.add(targetComment_0.githubCommentId);
      return nextIds_1;
    });
    if (editingCommentId === targetComment_0.githubCommentId) {
      setEditingCommentId(null);
      setEditDraft('');
    }
    if (activeReplyCommentId === targetComment_0.githubCommentId) {
      onReplyDraftChange('');
      onReplyCancel();
    }
    try {
      await deleteHandler(targetComment_0);
    } catch (error_0) {
      setLocallyHiddenCommentIds(previousIds_2 => {
        const nextIds_2 = new Set(previousIds_2);
        nextIds_2.delete(targetComment_0.githubCommentId);
        return nextIds_2;
      });
      // Re-open confirmation so users can retry.
      setPendingDeleteComment(targetComment_0);
      toast.error(error_0 instanceof Error ? error_0.message : 'Failed to delete comment');
    }
    // Runs on success and failure (catch swallows) — finally-equivalent, since
    // React Compiler can't lower try/finally yet.
    setDeletingCommentIds(previousIds_3 => {
      const nextIds_3 = new Set(previousIds_3);
      nextIds_3.delete(targetComment_0.githubCommentId);
      return nextIds_3;
    });
  }, [currentUserIdentifiers, deletingCommentIds, editingCommentId, mutationState.edit, onDeleteComment, onDeletePendingComment, activeReplyCommentId, onReplyCancel, onReplyDraftChange, pendingDeleteComment]);
  const handleCopyCommentLink = useCallback((githubCommentId: string) => {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    // Persist tab context so links opened in a new tab can mount the correct comment container.
    setPrV2CommentLink(url, {
      tab: anchorTab,
      commentId: githubCommentId
    });
    void copyToClipboard(url.toString(), {
      successMessage: 'Copied link',
      errorMessage: 'Failed to copy link'
    });
  }, [anchorTab]);
  const handleAddThreadToAiChat = useCallback(() => {
    const threadContext = buildPrV2ThreadAiChatText({
      comments: visibleComments,
      thread,
      surface: aiChatSurface
    });
    toggleSidebar(threadContext, 'append');
  }, [aiChatSurface, thread, toggleSidebar, visibleComments]);
  const handleGenerateFix = useCallback(async (comment_4: PrV2CommentResponse) => {
    if (!owner || !repo || !pullNumber) {
      toast.error('Missing PR information');
      return;
    }
    if (!installationId) {
      toast.error('Missing installation information');
      return;
    }
    if (!comment_4.path || typeof comment_4.line !== 'number') {
      toast.error('Fix generation requires file context');
      return;
    }
    const commentId_0 = parsePositiveInteger(comment_4.githubCommentId);
    if (commentId_0 == null) {
      toast.error('Fix generation requires a saved comment');
      return;
    }
    const toastId = toast.success(GENERATING_FIX_MESSAGE);
    trackFixButtonClicked({
      installationId,
      owner,
      repo,
      pullNumber,
      provider: aiReviewSettings?.autoFixProvider ?? DEFAULT_AUTO_FIX_PROVIDER,
      source: FIX_BUTTON_SOURCE.PR_V2_SINGLE_COMMENT,
      fixType: FIX_TYPE.SINGLE,
      commentId: commentId_0,
      filePath: comment_4.path,
      lineNumber: comment_4.line
    });
    try {
      const cleanContent = removeMetadataFromComment(comment_4.body ?? '');
      const response = await fetch('/api/fix/comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          owner,
          repo,
          pullNumber: Number(pullNumber),
          installationId,
          commentId: commentId_0,
          filename: comment_4.path,
          lineNumber: comment_4.line,
          content: cleanContent
        })
      });
      if (!response.ok) {
        const error_2 = await response.json().catch(() => null);
        const message = error_2 && typeof error_2 === 'object' && 'error' in error_2 && typeof error_2.error === 'string' ? error_2.error : 'Failed to start fix generation';
        // Handle inline instead of throw-to-catch: a throw inside try blocks
        // React Compiler memoization of the whole component.
        toast.dismiss(toastId);
        toast.error(message);
      } else {
        const result: unknown = await response.json().catch(() => null);
        // A cubic fix runs as a fix session; its sandbox is not known yet, so the toast
        // opens the fix sessions list, where it shows up once it starts.
        if (result && typeof result === 'object' && 'provider' in result && result.provider === 'cubic') {
          toast.success(GENERATING_FIX_MESSAGE, {
            id: toastId,
            action: {
              label: 'View fixes',
              onClick: () => window.open(FIX_SESSIONS_PATH, '_blank', 'noopener,noreferrer')
            }
          });
        }
      }
    } catch (error_1) {
      toast.dismiss(toastId);
      toast.error(error_1 instanceof Error ? error_1.message : 'Failed to generate fix');
    }
  }, [aiReviewSettings?.autoFixProvider, installationId, owner, pullNumber, repo]);
  const handleResolveToggle = useCallback(async (comment_5: PrV2CommentResponse) => {
    const nextResolved = !effectiveIsResolved;
    setLocalResolvedOverride(nextResolved);
    setIsResolvedExpanded(!nextResolved);
    try {
      await Promise.resolve(onResolveToggle(comment_5, nextResolved));
    } catch {
      setLocalResolvedOverride(null);
      setIsResolvedExpanded(!serverResolved);
    }
  }, [effectiveIsResolved, onResolveToggle, serverResolved]);
  const handleCopyPromptForAiAgents = useCallback((comment_6: PrV2CommentResponse) => {
    const raw = asRecord(comment_6.raw);
    const githubSide = raw?.side === 'LEFT' || raw?.start_side === 'LEFT' || raw?.original_side === 'LEFT' || thread?.diffSide === 'LEFT' || thread?.startDiffSide === 'LEFT' ? 'LEFT' : 'RIGHT';
    const buildPrompt = (fileContent: string | undefined) => buildCommentContext({
      comment: comment_6.body ?? '',
      filename: comment_6.path ?? undefined,
      lineNumber: typeof comment_6.line === 'number' ? comment_6.line : undefined,
      fileContent,
      diffHunk: aiPromptContext?.diffHunk ?? undefined,
      style: 'verbose'
    });
    // Resolving file contents can hit the network for stripped payloads: awaiting it before
    // the clipboard write would drop the transient user activation Safari/WebKit requires
    // for navigator.clipboard access.
    const resolvePromptFileContent = async (): Promise<string | undefined> => {
      let oldFileContent = aiPromptContext?.oldFileContent ?? null;
      let newFileContent = aiPromptContext?.newFileContent ?? null;
      if (aiPromptContext?.loadFileContents) {
        try {
          const loadedFileContents = await aiPromptContext.loadFileContents();
          if (loadedFileContents) {
            oldFileContent = loadedFileContents.oldFileContent ?? oldFileContent;
            newFileContent = loadedFileContents.newFileContent ?? newFileContent;
          }
        } catch {
          // Content fetch failed (stripped payload); degrade to the diff hunk instead of
          // blocking the copy on full file contents.
          oldFileContent = null;
          newFileContent = null;
        }
      }
      const fileContent_0 = githubSide === 'LEFT' ? oldFileContent ?? newFileContent ?? undefined : newFileContent ?? oldFileContent ?? undefined;
      return fileContent_0 ?? undefined;
    };
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      // Hand the pending promise to ClipboardItem synchronously so the write happens within
      // the click's user activation even though the payload resolves later.
      navigator.clipboard.write([new ClipboardItem({
        'text/plain': resolvePromptFileContent().then(fileContent_1 => new Blob([buildPrompt(fileContent_1)], {
          type: 'text/plain'
        }))
      })]).then(() => {
        toast.success('Copied prompt for AI agents');
      }).catch(() => {
        toast.error('Failed to copy prompt');
      });
      return;
    }

    // Fallback path (no ClipboardItem): the write itself must happen inside the click's
    // user activation. When file contents would require a network fetch, skip loading them
    // and copy the diff-hunk-only prompt synchronously instead of losing the activation.
    const requiresNetworkFileContents = Boolean(aiPromptContext?.loadFileContents) && aiPromptContext?.hasImmediateFileContents !== true;
    if (requiresNetworkFileContents) {
      void copyToClipboard(buildPrompt(undefined), {
        successMessage: 'Copied prompt for AI agents',
        errorMessage: 'Failed to copy prompt'
      });
      return;
    }
    void resolvePromptFileContent().then(fileContent_2 => copyToClipboard(buildPrompt(fileContent_2), {
      successMessage: 'Copied prompt for AI agents',
      errorMessage: 'Failed to copy prompt'
    }));
  }, [aiPromptContext, thread?.diffSide, thread?.startDiffSide]);
  const handleApplySuggestion = useCallback(async (comment_7: PrV2CommentResponse, payload: PrV2ApplySuggestionPayload) => {
    if (!onApplySuggestion || effectiveMutationState.any || suggestionApplyDisabledReasonOverride) {
      return false;
    }
    setApplyingSuggestionCommentId(comment_7.githubCommentId);
    setLocallyHiddenCommentIds(previousIds_4 => {
      const nextIds_4 = new Set(previousIds_4);
      nextIds_4.add(comment_7.githubCommentId);
      return nextIds_4;
    });
    const toastId_0 = toast.loading('Applying suggestion…');
    let applied = false;
    try {
      await onApplySuggestion(comment_7, payload);
      toast.success('Suggestion applied', {
        id: toastId_0
      });
      applied = true;
    } catch (error_3) {
      setLocallyHiddenCommentIds(previousIds_5 => {
        const nextIds_5 = new Set(previousIds_5);
        nextIds_5.delete(comment_7.githubCommentId);
        return nextIds_5;
      });
      toast.error(error_3 instanceof Error ? error_3.message : 'Failed to apply suggestion', {
        id: toastId_0
      });
    }
    // Runs on success and failure (catch swallows) — finally-equivalent, since
    // React Compiler can't lower try/finally yet.
    setApplyingSuggestionCommentId(current => current === comment_7.githubCommentId ? null : current);
    return applied;
  }, [effectiveMutationState.any, onApplySuggestion, suggestionApplyDisabledReasonOverride]);
  const handleApplySuggestionAndResolve = useCallback(async (comment_8: PrV2CommentResponse, payload_0: PrV2ApplySuggestionPayload) => {
    const applied_0 = await handleApplySuggestion(comment_8, payload_0);
    if (!applied_0 || effectiveIsResolved || comment_8.commentType !== 'review_comment') {
      return;
    }
    await handleResolveToggle(comment_8);
  }, [effectiveIsResolved, handleApplySuggestion, handleResolveToggle]);
  useEffect(() => {
    if (!activeReplyCommentId) {
      return;
    }
    if (comments.some(comment_9 => comment_9.githubCommentId === activeReplyCommentId)) {
      return;
    }
    onReplyDraftChange('');
    onReplyCancel();
  }, [activeReplyCommentId, comments, onReplyCancel, onReplyDraftChange]);
  useEffect(() => {
    if (!shouldRefocusReplyOnRestoreRef.current || isReplySubmitting || !replyDraft.trim()) {
      return;
    }
    shouldRefocusReplyOnRestoreRef.current = false;
    replyTextareaRef.current?.focus();
  }, [isReplySubmitting, replyDraft]);
  useEffect(() => {
    const currentThreadId = thread?.id ?? null;
    const previousThreadId = previousResolvedThreadIdRef.current;
    if (currentThreadId !== previousThreadId) {
      previousResolvedThreadIdRef.current = currentThreadId;

      // Thread disappeared transiently (multi-query refetch race) —
      // preserve current state until the thread reappears.
      if (currentThreadId === null && previousThreadId !== null) {
        previousResolvedThreadIdRef.current = previousThreadId;
        return;
      }

      // Active override means the user just toggled resolve/unresolve and
      // the thread identity flickered — keep the optimistic state.
      if (localResolvedOverride !== null) {
        previousEffectiveResolvedRef.current = effectiveIsResolved;
        return;
      }
      previousEffectiveResolvedRef.current = serverResolved;
      setIsResolvedExpanded(!serverResolved);
      return;
    }

    // Do NOT clear localResolvedOverride when serverResolved matches.
    // The cache-level optimistic flip makes them agree immediately, but
    // scheduleFallbackInvalidation can deliver a stale refetch that flips
    // serverResolved back before the server confirms.  Keeping the
    // override active prevents both resolve and unresolve flash.
    // The override naturally resets on component remount or thread change.

    const previousEffectiveResolved = previousEffectiveResolvedRef.current;
    if (!previousEffectiveResolved && effectiveIsResolved) {
      setIsResolvedExpanded(false);
    }
    previousEffectiveResolvedRef.current = effectiveIsResolved;
  }, [effectiveIsResolved, localResolvedOverride, serverResolved, thread?.id]);
  useExpandResolvedOnReveal({
    comments,
    shouldRenderResolvedSummary,
    setIsResolvedExpanded
  });
  const isPendingDeleteConfirmInFlight = Boolean(pendingDeleteComment && deletingCommentIds.has(pendingDeleteComment.githubCommentId));
  const isDeleteDialogActionPending = mutationState.edit || isPendingDeleteConfirmInFlight;
  useEffect(() => {
    if (!pendingDeleteComment || isDeleteDialogActionPending) {
      return;
    }
    const focusDeleteConfirm = () => {
      deleteConfirmRef.current?.focus();
    };

    // Opening from the overflow menu can restore focus back to the menu trigger
    // after the dialog mounts, so re-apply focus on the next macrotask.
    focusDeleteConfirm();
    const timeoutId = window.setTimeout(focusDeleteConfirm, 0);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isDeleteDialogActionPending, pendingDeleteComment]);
  useEffect(() => {
    if (!pendingDeleteComment || isDeleteDialogActionPending) {
      return;
    }

    // Radix AlertDialog intentionally prevents pointer-down-outside dismissal,
    // but this confirmation is expected to close when users click away.
    const handlePointerOutside = (event: MouseEvent | PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (deleteDialogContentRef.current?.contains(target)) {
        return;
      }
      setPendingDeleteComment(null);
    };
    document.addEventListener('mousedown', handlePointerOutside, true);
    document.addEventListener('pointerdown', handlePointerOutside, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerOutside, true);
      document.removeEventListener('pointerdown', handlePointerOutside, true);
    };
  }, [isDeleteDialogActionPending, pendingDeleteComment]);
  useEffect(() => {
    if (!editingCommentId) {
      return;
    }

    // Match the rest of the PR editing surfaces: opening comment edit mode
    // should keep focus in the textarea and place the caret at the end.
    const focusTextareaAtEnd = () => {
      const textarea = editTextareaRef.current;
      if (!textarea) {
        return;
      }
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    };
    const frameId = window.requestAnimationFrame(focusTextareaAtEnd);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [editingCommentId]);
  if (visibleComments.length === 0) {
    return null;
  }
  const resolvedByLabel = thread?.resolvedBy?.login ?? user?.user_metadata?.user_name ?? user?.email ?? 'Someone';
  const shouldHideReplyComposer = shouldRenderResolvedSummary && !isResolvedExpanded;
  const rootReplyComment = replyComposerPlacement === 'thread-footer' ? visibleComments.find(comment_10 => comment_10.commentType === 'review_comment' && canReplyToComment(comment_10) && canReplyToCommentId(comment_10.githubCommentId, allowPendingReplyTargets)) ?? null : null;
  const activeReplyComment = activeReplyCommentId ? visibleComments.find(comment_11 => comment_11.githubCommentId === activeReplyCommentId) ?? null : null;
  const isThreadFooterReplyComposerVisible = !isReadOnly && replyComposerPlacement === 'thread-footer' && !shouldHideReplyComposer && Boolean(activeReplyComment);
  const replyComposerTargetComment = isThreadFooterReplyComposerVisible && activeReplyComment ? activeReplyComment : null;
  const handleReplyComposerCancel = () => {
    onReplyDraftChange('');
    onReplyCancel();
  };
  const renderReplyComposer = (comment_12: PrV2CommentResponse, placement: 'inline' | 'footer') => {
    const userLabel = comment_12.userLogin ?? 'Unknown';
    const isBotComment = userLabel.endsWith('[bot]');
    const displayUserLabel = isBotComment ? userLabel.slice(0, -5) : userLabel;
    const isRootReplyTarget = rootReplyComment?.githubCommentId === comment_12.githubCommentId;
    const shouldShowReplyContext = placement === 'footer';
    const replyContextLabel = isRootReplyTarget ? null : `Replying to @${displayUserLabel}`;
    const replyAriaLabel = isRootReplyTarget ? 'Reply in thread' : `Reply to ${displayUserLabel}`;
    const placeholder = isMrgeBot(comment_12.userLogin) && replyComposerStyle === 'discussion-v1' ? 'Tag @cubic-dev-ai to share feedback...' : replyPlaceholder;
    const handleSubmitReply = async (text: string) => {
      const replyBody = text.trim();
      if (!replyBody) {
        return;
      }
      if (isReplyActionPending) {
        // AutosizeTextarea clears internal state after submit attempts; restore controlled value.
        onReplyDraftChange(text);
        return;
      }

      // Keep parent state in sync with AutosizeTextarea's internal clear-on-submit behavior.
      // Parent submit handlers own rollback and will restore the draft on failures.
      onReplyDraftChange('');
      setIsReplySubmitting(true);
      try {
        await Promise.resolve(onReplySubmit(comment_12.githubCommentId, text, replyDraftKey));
      } catch {
        shouldRefocusReplyOnRestoreRef.current = true;
      }
      // Runs on success and failure (catch swallows) — finally-equivalent, since
      // React Compiler can't lower try/finally yet.
      setIsReplySubmitting(false);
    };
    const composerBody = replyComposerStyle === 'discussion-v1' ? <div className="z-50 w-full py-0">
          <AutosizeTextarea ref={replyTextareaRef} owner={owner} repo={repo} data-hotkeys-ignore="true" className="w-full resize-none border-none bg-transparent px-0 py-1 pr-28 focus:outline-hidden md:text-base" name="comment-reply" aria-label={replyAriaLabel} placeholder={placeholder} autoFocus minHeight={20} maxHeight={400} variant={TEXTAREA_VARIANTS.reply} enableUserMentions disabled={isReplyActionPending} handleSubmit={handleSubmitReply} onChange={event_0 => {
        onReplyDraftChange(event_0.target.value);
      }} value={replyDraft} />
        </div> : <AutosizeTextarea ref={replyTextareaRef} owner={owner} repo={repo} data-hotkeys-ignore="true" className="w-full resize-none overflow-hidden rounded-lg border-border bg-zinc-900 px-0 py-1 font-sans" name="comment-reply" aria-label={replyAriaLabel} placeholder={replyPlaceholder} autoFocus minHeight={32} maxHeight={400} variant={TEXTAREA_VARIANTS.prReply} enableUserMentions disabled={isReplyActionPending} handleSubmit={handleSubmitReply} onChange={event_1 => {
      onReplyDraftChange(event_1.target.value);
    }} value={replyDraft} side="new" />;
    if (placement === 'footer') {
      return <div className="border-t border-zinc-700/40 px-3 py-3">
          {shouldShowReplyContext && replyContextLabel ? <div className="mb-2 flex items-center justify-between gap-3 text-[12px] text-zinc-400">
              <span>{replyContextLabel}</span>
              <button type="button" className="text-xs text-zinc-500 transition-colors hover:text-zinc-300" onClick={handleReplyComposerCancel}>
                Cancel
              </button>
            </div> : null}
          {composerBody}
        </div>;
    }
    return <div className="mt-3">
        {replyComposerStyle === 'discussion-v1' ? <>
            <Separator className="mb-4 text-zinc-800" />
            {composerBody}
          </> : <>
            <div className="border-t border-zinc-700/40 pt-3">{composerBody}</div>
            <div className="mt-2 flex justify-end">
              <button type="button" className="text-xs text-zinc-500 transition-colors hover:text-zinc-300" onClick={handleReplyComposerCancel}>
                Cancel
              </button>
            </div>
          </>}
      </div>;
  };
  return <TooltipProvider delayDuration={100}>
      <div data-testid={layout === 'inline' ? 'pr-v2-inline-thread-card' : 'pr-v2-thread-card'} className={cn('overflow-hidden rounded-lg border border-zinc-700/50 bg-zinc-900 font-sans shadow-sm', layout === 'inline' ? cn('mx-2 my-3 w-[calc(100%_-_1rem)] max-w-[calc(100%_-_1rem)]', isSplitDiffStyle ? 'sm:mx-4 sm:w-auto sm:max-w-[94%]' : 'sm:mx-4 sm:w-auto sm:max-w-[70%]') : 'w-full')}>
        {headline ? <div className={cn('flex min-h-8 items-center border-b border-zinc-700/40 px-3 py-1.5', headline.align === 'center' && 'justify-center text-center')}>
            <ThreadHeadlineLabel headline={headline} />
          </div> : null}

        {shouldRenderResolvedSummary && !isResolvedExpanded ? <button type="button" className="flex w-full items-center justify-between bg-zinc-900 px-3 py-2 text-left hover:bg-zinc-800/80" onClick={() => setIsResolvedExpanded(true)}>
            <span className="text-[12px] text-zinc-400">
              <span className="mr-2 text-green-700">✓</span>
              <span>{resolvedByLabel}</span>
              <span className="text-zinc-500"> resolved this</span>
            </span>
            <UnfoldVertical className="size-3.5 text-zinc-400" />
          </button> : null}

        {shouldRenderResolvedSummary && isResolvedExpanded ? <button type="button" className="flex w-full items-center justify-between border-b border-zinc-700/40 bg-zinc-900 px-3 py-2 text-left hover:bg-zinc-800/80" onClick={() => setIsResolvedExpanded(false)}>
            <span className="text-[12px] text-zinc-400">
              <span className="mr-2 text-green-700">✓</span>
              <span>{resolvedByLabel}</span>
              <span className="text-zinc-500"> resolved this</span>
            </span>
            <FoldVertical className="size-3.5 text-zinc-400" />
          </button> : null}

        <div className={cn('divide-y divide-zinc-700/30', shouldRenderResolvedSummary && !isResolvedExpanded && 'hidden')}>
          {/* eslint-disable-next-line complexity */}
          {visibleComments.map((comment_13, index) => {
          const userLabel_0 = comment_13.userLogin ?? 'Unknown';
          const isBotComment_0 = userLabel_0.endsWith('[bot]');
          const displayUserLabel_0 = isBotComment_0 ? userLabel_0.slice(0, -5) : userLabel_0;
          const commentCreatedAt = getCommentCreatedAt(comment_13);
          const ageLabel = formatCommentAge(getCommentCreatedAt(comment_13));
          const exactTimeLabel = formatCommentTimestamp(commentCreatedAt);
          const avatarColor = getAvatarColor(userLabel_0);
          const canReact = !isReadOnly && reactions && (reactions.canReactToComment?.(comment_13) ?? true);
          const hasVisibleReactions = reactions ? getVisibleReactionEmojis(comment_13, reactions!).length > 0 : false;
          const shouldEnableHoverActionFocusTarget = reactionAffordancePlacement === 'hover-strip' && canReact && !hasVisibleReactions;
          const canSeeInChanges = onSeeInChanges && (shouldShowSeeInChanges?.(comment_13) ?? true);
          const manageMode_2 = getCommentManageMode(comment_13, currentUserIdentifiers);
          const lifecycle = getPrV2CommentLifecycle(comment_13);
          const isQueuedComment = lifecycle === 'queued';
          const isSubmittingComment = lifecycle === 'submitting';
          const isNonCanonicalComment = lifecycle !== 'persisted';
          const shouldShowPendingBadge = isQueuedComment && anchorTab === 'changes';
          const canCopyLink = !isNonCanonicalComment && parsePositiveInteger(comment_13.githubCommentId) != null;
          const aiAgentPrompt = extractAiAgentPromptText(comment_13.body);
          const canCopyPromptForAiAgents = Boolean(comment_13.body?.trim());
          const isOutdatedThreadComment = Boolean(thread?.isOutdated && index === 0);
          const confidence = getCommentConfidence(comment_13.body);
          const severity = severityByCommentId?.[comment_13.githubCommentId];
          const priority = typeof severity === 'number' ? severityToPriority(severity) : null;
          const displayBody = removeLeadingPriorityPrefix(comment_13.body, priority);
          const commentLinkId = getPrV2CommentAnchorId(anchorKind, comment_13.githubCommentId);
          const isEditing = editingCommentId === comment_13.githubCommentId;
          const canShowEdit = !isReadOnly && Boolean(!isEditing && (manageMode_2 === 'persisted' && onEditComment || manageMode_2 === 'pending' && onEditPendingComment));
          const canShowDelete = !isReadOnly && Boolean(!isEditing && (manageMode_2 === 'persisted' && onDeleteComment || manageMode_2 === 'pending' && onDeletePendingComment));
          const shouldShowDeleteInOverflow = overflowUtilityActions && canShowDelete;
          const shouldShowInlineDelete = canShowDelete && !shouldShowDeleteInOverflow;
          const isCommentDeletePending = deletingCommentIds.has(comment_13.githubCommentId);
          const canReply = !isReadOnly && canReplyToComment(comment_13) && canReplyToCommentId(comment_13.githubCommentId, allowPendingReplyTargets);
          const isReplyComposerVisible = !shouldHideReplyComposer && activeReplyCommentId === comment_13.githubCommentId;
          const shouldShowCommentReplyButton = canReply;
          const shouldRenderInlineReplyComposer = !isReadOnly && replyComposerPlacement === 'inline-comment' && isReplyComposerVisible;
          const shouldRenderDiscussionPreview = codePreviewMode === 'discussion-inline' && index === 0 && comment_13.commentType === 'review_comment';
          const hasGenerateFixContext = Boolean(aiReviewSettings?.autoFixButtonEnabled && !isNonCanonicalComment && comment_13.path && typeof comment_13.line === 'number' && parsePositiveInteger(comment_13.githubCommentId) != null);
          const isCodingAgentsAccessLocked = !isCodingAgentsAccessLoading && !isCodingAgentsAccessError && !canUseCodingAgents;
          const canGenerateFix = !isReadOnly && canUseCodingAgents && hasGenerateFixContext;
          const canShowLockedGenerateFix = !isReadOnly && isCodingAgentsAccessLocked && hasGenerateFixContext;
          const hasOverflowUtilityItems = canSeeInChanges || canGenerateFix || canShowLockedGenerateFix || canCopyLink;
          // Keep copy-prompt visible for canonical comments; queued/submitting comments do not.
          const canShowCopyPromptForAiAgents = !isReadOnly && !isNonCanonicalComment && canCopyPromptForAiAgents;
          const shouldShowOverflowUtilityMenu = Boolean(overflowUtilityActions && (hasOverflowUtilityItems || shouldShowDeleteInOverflow));
          const effectiveSuggestionApplyDisabledReason = suggestionApplyDisabledReasonOverride ?? (onApplySuggestion && isQueuedComment ? 'Cannot apply suggestion while comment is pending review' : onApplySuggestion && isSubmittingComment ? 'Cannot apply suggestion until comment finishes syncing' : onApplySuggestion && (comment_13.commentType !== 'review_comment' || !comment_13.path) ? 'Suggestion requires a review comment with file context' : undefined);
          return <div key={getCommentRenderKey(comment_13)} id={commentLinkId} className={cn('group/comment px-3 py-2.5', shouldEnableHoverActionFocusTarget && 'focus:outline-none')} tabIndex={shouldEnableHoverActionFocusTarget ? 0 : undefined}>
                <div className="flex items-start gap-2">
                  <div className={`mt-1 size-4 shrink-0 overflow-hidden rounded-full ${comment_13.userAvatarUrl ? 'bg-zinc-100 ring-1 ring-inset ring-black/15' : avatarColor}`}>
                    {comment_13.userAvatarUrl ? <span aria-label={userLabel_0} className="block h-full w-full bg-cover bg-center" style={{
                  backgroundImage: `url(${comment_13.userAvatarUrl})`
                }} /> : <span className="flex h-full w-full items-center justify-center text-[10px] font-medium text-white">
                        {userLabel_0.slice(0, 1).toUpperCase()}
                      </span>}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex min-h-6 items-start justify-between gap-2">
                      <div className="min-w-0 flex min-h-6 flex-wrap items-center gap-x-1.5 gap-y-1">
                        {/* Keep author/timestamp vertically stable while optimistic actions mount in later. */}
                        <div className="inline-flex min-h-6 shrink-0 items-center gap-1.5 whitespace-nowrap">
                          <PrV2UserNameLabel login={userLabel_0} displayText={displayUserLabel_0} className="text-[13px] leading-5 font-medium text-zinc-200" />
                          {ageLabel ? <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-default text-[11px] leading-5 text-zinc-500">
                                  {ageLabel}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>{exactTimeLabel}</TooltipContent>
                            </Tooltip> : null}
                        </div>
                        {shouldShowPendingBadge ? <span className="rounded-full bg-orange-900/40 px-2 py-0.5 text-[10px] font-medium text-orange-400">
                            Pending
                          </span> : null}
                        {isBotComment_0 ? <span className={THREAD_METADATA_PILL_CLASSNAME}>Bot</span> : null}
                        {priority ? <span className={THREAD_METADATA_PILL_CLASSNAME}>{priority}</span> : null}
                        {isOutdatedThreadComment ? <span className={THREAD_METADATA_PILL_CLASSNAME}>Outdated</span> : null}
                        {confidence === 10 ? <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center rounded-full border border-red-400/30 bg-red-900/40 px-1.5 py-0.5 text-red-400">
                                <ChevronsUpIcon className="size-3.5" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>High confidence</TooltipContent>
                          </Tooltip> : null}
                      </div>
                      <div className="flex shrink-0 items-center">
                        <div className={cn('invisible opacity-0 transition-opacity duration-50 ease-out', 'group-hover/comment:visible group-hover/comment:opacity-100', 'group-focus-within/comment:visible group-focus-within/comment:opacity-100', 'focus-within:visible focus-within:opacity-100')}>
                          <div className="flex shrink-0 items-center gap-2 pr-2">
                            {shouldShowOverflowUtilityMenu ? <DropdownMenu modal={false}>
                                <ThreadTooltip content="More actions">
                                  <DropdownMenuTrigger asChild>
                                    <button type="button" aria-label="More actions" className="inline-flex size-6 items-center justify-center rounded-md border border-transparent text-zinc-500 transition-colors hover:bg-zinc-800/50 hover:text-zinc-300">
                                      <MoreHorizontal className={THREAD_ACTION_ICON_CLASSNAME} />
                                    </button>
                                  </DropdownMenuTrigger>
                                </ThreadTooltip>
                                <DropdownMenuContent align="end" className="min-w-[220px]">
                                  {canSeeInChanges ? <DropdownMenuItem onSelect={() => onSeeInChanges?.(comment_13)}>
                                      <Eye className="size-3.5 text-zinc-400" />
                                      See in changes
                                    </DropdownMenuItem> : null}
                                  {canGenerateFix ? <DropdownMenuItem onSelect={() => {
                              void handleGenerateFix(comment_13);
                            }}>
                                      <Sparkles className="size-3.5 text-zinc-400" />
                                      {fixProviderCopy.fixWithLabel}
                                    </DropdownMenuItem> : null}
                                  {canShowLockedGenerateFix ? <ThreadTooltip content={<CodingAgentsUpgradeTooltipContent />} side="left" align="end">
                                      <DropdownMenuItem onSelect={event_2 => {
                                event_2.preventDefault();
                              }} className="cursor-not-allowed text-zinc-500">
                                        <Lock className="size-3.5 text-zinc-500" />
                                        {fixProviderCopy.fixWithLabel}
                                      </DropdownMenuItem>
                                    </ThreadTooltip> : null}
                                  {canCopyLink ? <DropdownMenuItem onSelect={() => handleCopyCommentLink(comment_13.githubCommentId)}>
                                      <Link2 className="size-3.5 text-zinc-400" />
                                      Copy link
                                    </DropdownMenuItem> : null}
                                  {shouldShowDeleteInOverflow ? <DropdownMenuItem onSelect={() => handleRequestDelete(comment_13)} disabled={isCommentDeletePending || mutationState.edit}>
                                      <Trash2 className="size-3.5 text-zinc-400" />
                                      Delete comment
                                    </DropdownMenuItem> : null}
                                </DropdownMenuContent>
                              </DropdownMenu> : null}
                            {canShowCopyPromptForAiAgents ? <ThreadTooltip content="Copy prompt for AI agents">
                                <button type="button" aria-label="Copy prompt for AI agents" className="inline-flex size-6 items-center justify-center rounded-md border border-transparent text-zinc-500 transition-colors hover:bg-zinc-800/50 hover:text-zinc-300" onClick={() => {
                            if (aiAgentPrompt) {
                              void copyToClipboard(aiAgentPrompt, {
                                successMessage: 'Copied prompt for AI agents',
                                errorMessage: 'Failed to copy prompt'
                              });
                              return;
                            }
                            void handleCopyPromptForAiAgents(comment_13);
                          }}>
                                  <Copy className={THREAD_ACTION_ICON_CLASSNAME} />
                                </button>
                              </ThreadTooltip> : null}
                            {index === 0 && !isReadOnly ? <ThreadTooltip content="Add to AI chat">
                                <button type="button" aria-label="Add to AI chat" className="inline-flex size-6 items-center justify-center rounded-md border border-transparent text-zinc-500 transition-colors hover:bg-zinc-800/50 hover:text-zinc-300" onClick={handleAddThreadToAiChat}>
                                  <Bot className={THREAD_ACTION_ICON_CLASSNAME} />
                                </button>
                              </ThreadTooltip> : null}
                            {canShowEdit ? <ThreadTooltip content="Edit comment">
                                <button type="button" aria-label="Edit comment" className="inline-flex size-6 items-center justify-center rounded-md border border-transparent text-zinc-500 transition-colors hover:bg-zinc-800/50 hover:text-zinc-300" onClick={() => handleStartEditing(comment_13)} disabled={isEditActionPending}>
                                  <Pencil className={THREAD_ACTION_ICON_CLASSNAME} />
                                </button>
                              </ThreadTooltip> : null}
                            {reactionAffordancePlacement === 'hover-strip' && canReact ? <ReactionPickerTrigger comment={comment_13} reactions={reactions!} variant="icon" pickerAlign="end" pickerSide="bottom" tooltipContent="Add reaction" /> : null}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {!overflowUtilityActions && canSeeInChanges ? <ThreadTooltip content="See in changes">
                              <button type="button" aria-label="See in changes" className="inline-flex size-6 items-center justify-center rounded-md border border-transparent text-zinc-500 transition-colors hover:bg-zinc-800/50 hover:text-zinc-300" onClick={() => onSeeInChanges?.(comment_13)}>
                                <Eye className="size-3.5" />
                              </button>
                            </ThreadTooltip> : null}
                          {!overflowUtilityActions && canCopyLink ? <ThreadTooltip content="Copy link">
                              <button type="button" aria-label="Copy link" className="inline-flex size-6 items-center justify-center rounded-md border border-transparent text-zinc-500 transition-colors hover:bg-zinc-800/50 hover:text-zinc-300" onClick={() => handleCopyCommentLink(comment_13.githubCommentId)}>
                                <Link2 className="size-3.5" />
                              </button>
                            </ThreadTooltip> : null}
                          {!overflowUtilityActions && canGenerateFix ? <ThreadTooltip content={fixProviderCopy.fixWithLabel}>
                              <button type="button" aria-label="Generate fix" className="inline-flex size-6 items-center justify-center rounded-md border border-transparent text-zinc-500 transition-colors hover:bg-zinc-800/50 hover:text-zinc-300" onClick={() => {
                          void handleGenerateFix(comment_13);
                        }}>
                                <Sparkles className="size-3.5" />
                              </button>
                            </ThreadTooltip> : null}
                          {!overflowUtilityActions && canShowLockedGenerateFix ? <ThreadTooltip content={<CodingAgentsUpgradeTooltipContent />}>
                              <span>
                                <button type="button" aria-label={fixProviderCopy.fixWithRequiresProAriaLabel} className="inline-flex size-6 cursor-not-allowed items-center justify-center rounded-md border border-transparent text-zinc-600" disabled>
                                  <Lock className="size-3.5" />
                                </button>
                              </span>
                            </ThreadTooltip> : null}
                          {shouldShowCommentReplyButton ? <ThreadTooltip content="Reply">
                              <button type="button" aria-label="Reply" className={cn('inline-flex size-6 items-center justify-center rounded-md border border-transparent transition-colors', isReplyComposerVisible ? 'bg-zinc-800/40 text-zinc-300 hover:bg-zinc-800/60' : 'text-zinc-500 hover:bg-zinc-800/50 hover:text-zinc-300')} onClick={() => onReplySelect(comment_13)} disabled={isReplyActionPending}>
                                <Reply className="size-3.5" />
                              </button>
                            </ThreadTooltip> : null}
                          {shouldShowInlineDelete ? <ThreadTooltip content="Delete comment">
                              <button type="button" aria-label="Delete comment" className="inline-flex size-6 items-center justify-center rounded-md border border-transparent text-zinc-500 transition-colors hover:bg-zinc-800/50 hover:text-zinc-300" onClick={() => handleRequestDelete(comment_13)} disabled={isCommentDeletePending || mutationState.edit}>
                                <Trash2 className="size-3.5" />
                              </button>
                            </ThreadTooltip> : null}
                          {thread && index === 0 && comment_13.commentType === 'review_comment' && !isReadOnly ? <ThreadTooltip content={effectiveIsResolved ? 'Unresolve thread' : 'Resolve thread'}>
                              <button type="button" aria-label={effectiveIsResolved ? 'Unresolve thread' : 'Resolve thread'} aria-pressed={effectiveIsResolved} className={cn('inline-flex size-6 items-center justify-center rounded-md border border-transparent transition-colors', effectiveIsResolved ? 'bg-zinc-800/40 text-zinc-300 hover:bg-zinc-800/60' : 'text-zinc-500 hover:bg-zinc-800/50 hover:text-zinc-300')} onClick={() => void handleResolveToggle(comment_13)}>
                                <Check className="size-3.5" />
                              </button>
                            </ThreadTooltip> : null}
                        </div>
                      </div>
                    </div>
                    {isEditing ? <div className="mt-1.5">
                        <Textarea ref={editTextareaRef} value={editDraft} onChange={event_3 => setEditDraft(event_3.target.value)} onPaste={handleEditPaste} placeholder="Edit comment..." aria-label="Edit comment" className="min-h-[84px] border-zinc-700/50 bg-zinc-900 font-sans text-[13px] text-zinc-200 placeholder:text-zinc-500 focus-visible:ring-zinc-500" onKeyDown={event_4 => {
                    if ((event_4.metaKey || event_4.ctrlKey) && event_4.key === 'Enter') {
                      event_4.preventDefault();
                      void handleSubmitEdit();
                      return;
                    }
                    if (event_4.key === 'Escape') {
                      event_4.preventDefault();
                      handleCancelEditing();
                    }
                  }} autoFocus />
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <button type="button" className="text-xs text-zinc-500 transition-colors hover:text-zinc-300" onClick={handleCancelEditing}>
                            Cancel
                          </button>
                          <Button type="button" size="sm" className="h-7 text-xs" disabled={isEditActionPending || !editDraft.trim()} onClick={() => {
                      void handleSubmitEdit();
                    }}>
                            Save
                          </Button>
                        </div>
                      </div> : <>
                        <PrV2CommentContent owner={owner} repo={repo} pullNumber={pullNumber} filePath={comment_13.path ?? thread?.path ?? undefined} body={displayBody} userLogin={comment_13.userLogin} inReplyToId={comment_13.inReplyToId} id={`${commentLinkId}-content`} isPublicView={isReadOnly} className={THREAD_COMMENT_MARKDOWN_CLASS} emptyText={getEmptyTextForComment?.(comment_13) ?? '(no content)'} suggestionOriginalLines={getSuggestionOriginalLines(comment_13.raw)} onApplySuggestion={!isReadOnly && onApplySuggestion ? async suggestion => {
                    await handleApplySuggestion(comment_13, suggestion);
                  } : undefined} onApplySuggestionAndResolve={!isReadOnly && onApplySuggestion && thread && !effectiveIsResolved && comment_13.commentType === 'review_comment' ? suggestion_0 => handleApplySuggestionAndResolve(comment_13, suggestion_0) : undefined} applyDisabledReason={effectiveSuggestionApplyDisabledReason} isApplyingSuggestion={applyingSuggestionCommentId === comment_13.githubCommentId} />

                        {shouldRenderDiscussionPreview ? <PrV2ThreadCodePreview comment={comment_13} thread={thread} /> : null}
                        {reactions && (canReact || hasVisibleReactions) ? <ReactionFooter comment={comment_13} reactions={reactions!} allowToggleReactions={Boolean(canReact)} showAddReactionTrigger={reactionAffordancePlacement === 'footer-row' && Boolean(canReact)} /> : null}
                        {shouldRenderInlineReplyComposer ? renderReplyComposer(comment_13, 'inline') : null}
                      </>}
                  </div>
                </div>
              </div>;
        })}
          {replyComposerPlacement === 'thread-footer' && rootReplyComment && !shouldHideReplyComposer && replyComposerTargetComment ? renderReplyComposer(replyComposerTargetComment, 'footer') : null}
        </div>

        <AlertDialog open={Boolean(pendingDeleteComment)} onOpenChange={open => {
        if (!open) {
          setPendingDeleteComment(null);
        }
      }}>
          <AlertDialogContent ref={deleteDialogContentRef} onOpenAutoFocus={event_5 => {
          // Only this delete dialog should treat Enter as confirm by default.
          // Prevent Radix from moving focus to Cancel so Enter targets Delete.
          event_5.preventDefault();
          deleteConfirmRef.current?.focus();
        }} onKeyDown={event_6 => {
          if (event_6.key !== 'Enter' || isDeleteDialogActionPending) {
            return;
          }
          const activeElement = document.activeElement;
          if (activeElement === deleteConfirmRef.current) {
            event_6.preventDefault();
            deleteConfirmRef.current?.click();
            return;
          }
          if (activeElement instanceof HTMLButtonElement || activeElement instanceof HTMLAnchorElement || activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement || activeElement instanceof HTMLSelectElement) {
            return;
          }
          event_6.preventDefault();
          deleteConfirmRef.current?.click();
        }}>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete comment?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the comment from the thread.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleteDialogActionPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-zinc-100 text-zinc-900 hover:bg-zinc-200" disabled={isDeleteDialogActionPending} autoFocus ref={deleteConfirmRef} onClick={event_7 => {
              event_7.preventDefault();
              void handleConfirmDelete();
            }}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>;
}